import React, { Suspense, useLayoutEffect, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useWrapsToRows } from "./useWrapsToRows";

/** Generic panel placeholder while a lazy tab chunk loads. */
export const TabPanelFallback = () => (
  <div className="space-y-4" role="status" aria-label="Loading tab">
    <Skeleton className="h-8 w-[36%]" />
    <Skeleton className="h-[140px] w-full rounded-md" />
    <div className="grid gap-3 sm:grid-cols-2">
      <Skeleton className="h-24 w-full rounded-md" />
      <Skeleton className="h-24 w-full rounded-md" />
    </div>
    <Skeleton className="h-20 w-full rounded-md" />
  </div>
);

export interface TabConfig {
  /** Stable identifier used as the Radix tab value. Reordering tabs does not
   *  invalidate persisted selections because the key, not the index, is stored. */
  key: string;
  label: string | React.ReactNode;
  icon: React.ReactElement;
  panel: React.ReactNode;
  /** Greys out the trigger and prevents activation. */
  disabled?: boolean;
  /** Renders a small red dot next to the label: the tab's content needs attention. */
  badge?: boolean;
}

interface AppDetailTabsProps {
  tabs: TabConfig[];
  activeTab: string;
  onTabChange: (newKey: string) => void;
  /** Controls at the right end of the tab row, available from every tab. */
  actions?: React.ReactNode;
}

/** Tab rows at which the tab bar turns into a dropdown. */
const DROPDOWN_ROWS = 3;

/** Icon, title and attention dot; children of a flex row in both the tabs and the dropdown. */
const TabTitle = ({ tab }: { tab: TabConfig }) => (
  <>
    <span className="flex h-5 w-5 shrink-0 items-center [&_svg]:h-4 [&_svg]:w-4">{tab.icon}</span>
    {tab.label}
    {tab.badge && (
      <span
        role="img"
        aria-label="Needs attention"
        className="ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive"
      />
    )}
  </>
);

const AppDetailTabs: React.FC<AppDetailTabsProps> = ({ tabs, activeTab, onTabChange, actions }) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLButtonElement>(null);
  // The tab list stays laid out at the same width in both views, so the
  // measurement never depends on which view is showing.
  const dropdown = useWrapsToRows(listRef, DROPDOWN_ROWS);

  // Focus left on the view that just hid would drop to the page; move it across.
  useLayoutEffect(() => {
    const hidden = dropdown ? listRef.current : selectRef.current;
    const shown = dropdown
      ? selectRef.current
      : listRef.current?.querySelector<HTMLElement>('[data-state="active"]');
    if (hidden?.contains(document.activeElement)) shown?.focus();
  }, [dropdown]);

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="mb-6">
      <div className="flex flex-wrap items-start gap-x-2 border-b border-border/60 bg-slate-50/60 px-2">
        <div className="relative min-w-0 flex-1">
          <div
            aria-hidden={dropdown || undefined}
            className={cn(dropdown && "invisible absolute inset-x-0 top-0 h-0 overflow-hidden")}
          >
            <TabsList
              ref={listRef}
              className="mb-0 flex h-auto w-full flex-wrap justify-start gap-0 rounded-b-none border-b-0"
            >
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.key}
                  value={tab.key}
                  disabled={tab.disabled}
                  className={cn(
                    "gap-2 px-3 py-3 data-[state=active]:bg-transparent",
                    tab.disabled && "cursor-not-allowed opacity-40"
                  )}
                >
                  <TabTitle tab={tab} />
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className={cn("py-1.5", !dropdown && "hidden")}>
            <Select value={activeTab} onValueChange={onTabChange}>
              <SelectTrigger ref={selectRef} aria-label="Section" className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tabs.map((tab) => (
                  <SelectItem key={tab.key} value={tab.key} disabled={tab.disabled}>
                    <span className="flex items-center gap-2">
                      <TabTitle tab={tab} />
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {actions && <div className="ml-auto py-1.5">{actions}</div>}
      </div>
      {tabs.map((tab) => (
        <TabsContent
          key={tab.key}
          value={tab.key}
          className="mt-0 border border-t-0 bg-card p-6 shadow-card"
        >
          <Suspense fallback={<TabPanelFallback />}>{tab.panel}</Suspense>
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default AppDetailTabs;
